import Backbone from 'backbone'
import template from './datasets-date-filter-picker.hbs'
import valuesOrEquationsTemplate from 'scoreboard/datasets/filters/datasets-datetime-values-or-equations.hbs'
import _ from 'lodash'
import calendars from 'scoreboard/components/calendars'
import LabeledToggle from 'scoreboard/components/labeled-toggle'
import formHelper from 'scoreboard/components/form-helper'
import Dataset from 'scoreboard/datasets/model/dataset'
const { ALL_TIME } = Dataset
import CalendarRangeSelector from 'scoreboard/components/calendar-range-selector'
import { fixBadDates } from 'scoreboard/components/calendar-or-date-range-selector'
import { renderValueOrEquations } from './value-or-equation'
import deleteNonApplicableFilterProperties from './delete-non-applicable-filter-properties'
import i18n from 'scoreboard/components/i18n'
import DataType from 'scoreboard/datasets/model/data-type'
const { DATE, TIME, DATE_TIME, CALENDAR_PERIOD } = DataType

const DatasetsDateFilterPicker = Backbone.View.extend({
	className: 'panel date-filter-picker',

	events: {
		'input [name=date1], [name=date2]': 'fixBadDates'
	},

	/** @type {Dataset} */
	model: null,

	/**
	 * @constructs a view to pick the periods/dates to be used in a dataset filter
	 * on a date field
	 *
	 * @param {Object} options.filter the filter we're working with
	 *
	 * @param {FormView} options.formView the FormView that encloses this view
	 *
	 * @param {boolean} [allowIncomplete=false] whether the form can be saved without
	 * completing the filter (ie an empty date)
	 *
	 * @param {boolean} [allowAppCalendar=false] whether Current Calendar is
	 * available as a Calendar option
	 *
	 * @param {boolean} [allowAllTime=false] whether All Time is available as
	 * a Calendar option
	 *
	 * @param {boolean} [options.allowChooseByDay=true] whether the Day toggle is
	 * rendered to allow picking a date instead of a calendar period
	 *
	 * @param {boolean} [options.allowCalculated=true] whether to allow calculated values
	 */
	initialize: function(options) {
		this.options = options || {}
		_.defaults(this.options, {
			allowChooseByDay: true
		})
		this.field = this.getFilterField()
		this.formView = options.formView
	},

	render: function () {
		this.$el.empty().append(template({
			between: this._isBetweenDateTime()
		}))

		this.renderToggle()
		this.renderRangeSelector()

		formHelper.writeToDom(this.options.filter, this.el)

		this.renderDateValueOrEquations()

		_.invokeMap(this.dateValueOrEquations, 'changeUpdateType')
		this._resetClassNames(this.toggle.val())
		this._setupListeners()
		return this
	},

	renderDateValueOrEquations: function() {
		this._insertDateValueOrEquationsMarkup()
		this.dateValueOrEquations = renderValueOrEquations({
			dataType: DATE,
			model: this.model,
			filter: this.options.filter,
			formView: this.formView,
			field: this.field,
			el: this.$el,
			allowCalculated: this.options.allowCalculated,
			selector: '.date-value-or-equation'
		})
	},

	_insertDateValueOrEquationsMarkup: function() {
		const firstInputLabel = this._isBetweenDateTime() ?
			i18n.get('report.customReport.filter.from') :
				i18n.get('common.date')
		this.$('.toggler-rows.filter-type').append(valuesOrEquationsTemplate({
			firstInputLabel,
			type: DATE,
			namePrefix: DATE,
			classes: 'day-filter'
		}))
	},

	_isBetweenDateTime() {
		return this.options.filter.operator === 'between-date-time'
	},

	_setupListeners() {
		this.dateValueOrEquations.forEach(voe => {
			this.listenTo(voe, 'equationDialog:open', () => {
				this.trigger('equationDialog:open')
			})

			this.listenTo(voe, 'equationDialog:close', () => {
				this.trigger('equationDialog:close')
			})
		})
		this.listenTo(this.toggle, 'change', opt => {
			this._resetClassNames(opt)
			this.formView.clearErrors()
			this.trigger('toggleChooseBy', opt)
			this.trigger('change')
		})
		this.listenTo(this.rangeSelector, 'change', () => {
			this.trigger('change')
		})
	},

	_resetClassNames(opt) {
		let clearClass = reg => {
			return this.$el.removeClass((i, c) => (c.match(reg) || []).join(' '))
		}
		// Reset/append the correct 'is-showing-' classes based on the selected option
		clearClass(/(^|\s)is-showing-\S+/g).addClass(`is-showing-${opt}`)
		this.$el.toggleClass('is-between', this._isBetweenDateTime())
	},

	renderToggle: function() {
		var toggleVal = this._getToggleValue()
		this.toggle = new LabeledToggle({
			options: this._getToggleOptions(),
			value: toggleVal
		})
		this.$('.toggle-container').append(this.toggle.el)
		this.toggle.render()

		if (!this.options.allowChooseByDay) {
			this.$('.choose-by').addClass('is-not-displayed')
		}
	},

	_getToggleValue() {
		let filter = this.options.filter

		if (filter.dataType) {
			switch (filter.dataType) {
				case 'calendar-period': return CALENDAR_PERIOD
				case 'date': return 'day'
				case 'time': return TIME
				case 'date-time': return DATE_TIME
			}
		}

		if (filter.updateType1 === 'calculated' || filter.dateTime1 || filter.date1) {
			if (filter.dateTime1) {
				return DATE_TIME
			} else {
				return 'day'
			}
		} else return CALENDAR_PERIOD
	},

	_getToggleOptions() {
		return [{
			value: CALENDAR_PERIOD,
			label: 'common.calendar.calendarPeriod'
		},{
			value: 'day',
			label: 'common.date'
		}]
	},

	renderRangeSelector: function() {
		let filter = this.options.filter

		let range = {
			calendarId: filter.calendarId
		}

		if (_.isNil(range.calendarId) && !this.options.allowAppCalendar) {
			range.calendarId = calendars.calendars[0].id
		}

		if (!_.isNil(filter.periodId1)) {
			range.relative = false
			range.first = filter.periodId1
			range.last = filter.periodId2
		} else if (!_.isNil(filter.periodDelta1)) {
			range.relative = true
			range.first = _.isNil(filter.periodDelta1) ? 0 : filter.periodDelta1
			range.last = _.isNil(filter.periodDelta2) ? 0 : filter.periodDelta2
		} else {
			range.relative = !!this.options.allowAppCalendar
		}

		// #46389 populate default values for range for calendar period data types
		if (this.getFilterDataType() === CALENDAR_PERIOD) {
			range.first = range.first || 0
			range.last = range.last || 0
		}

		this.rangeSelector = new CalendarRangeSelector({
			range,
			canChangeCalendar: true,
			singlePeriod: !this._isBetweenDateTime(),
			allowAllTime: this.options.allowAllTime,
			allowAppCalendar: !!this.options.allowAppCalendar,
			rangeLabels: {
				singular: 'common.calendar.calendarPeriod',
				plural: 'common.calendar.calendarPeriods'
			}
		})

		this.$('.choose-by').after(this.rangeSelector.el)
		this.rangeSelector.render()
		this.rangeSelector.$el.addClass('calendar-period-filter choose-by-filter')
	},

	_fixBadDatesIsIrrelevant() {
		return !this._isBetweenDateTime() ||
			this.$('[name=updateType1]').val() !== 'manual' ||
			this.$('[name=updateType2]').val() !== 'manual'
	},

	fixBadDates: function(e) {
		if (this._fixBadDatesIsIrrelevant()) {
			return
		}

		fixBadDates(this.dateValueOrEquations[0].markedField, this.dateValueOrEquations[1].markedField, e)
	},

	chooseByVal: function () {
		return this.options.allowChooseByDay ? this.toggle.val() : 'calendar-period'
	},

	val: function() {
		let byDate = this.chooseByVal() === 'day'

		// #14711 don't try to pick specific fields because you might miss some
		// (e.g. breakdowns weren't being picked properly before)
		var filter = this.options.filter
		// Set the dataType according to the toggle value (#54272)
		filter.dataType = this.toggle.val() === CALENDAR_PERIOD ? CALENDAR_PERIOD : DATE

		if (byDate) {
			let fromDom = formHelper.readFromDom(this.el)
			_.extend(filter, _.omit(fromDom, 'date1', 'date2'))

			_.forEach([1,2], i => {
				// date1 and date2 come from date inputs, and the returned date
				// will be null if the date isn't valid (like after each keystroke
				// while typing in a date). The exact way we handle null depends
				// on the situation
				let key = DATE + i

				if (_.isNil(fromDom[key])) {
					// If the date was read as "null", it could either be empty
					// or not a valid date (like '1/2/' while in the middle of
					// typing).
					let rawVal = this.$(`[name=${key}]`).val().trim()
					if (rawVal === '') {
						// If the raw value read from the input is an empty string,
						// then the user must have intentionall cleared out the date.
						// In that case, set the filter's date to null
						filter[key] = null
					}

					// If the raw value isn't null, then they are probably in the
					// middle of entering a date manually. Leave the filter's date
					// alone until they have either cleared the input or finished
					// typing a proper date
				} else {
					// If a proper date was entered, use it to update the filter
					filter[key] = fromDom[key]
				}
			})

			deleteNonApplicableFilterProperties(this.options.filter, this.chooseByVal())
		} else {
			deleteNonApplicableFilterProperties(this.options.filter, this.chooseByVal())

			let val = this.rangeSelector.val()

			if (val.calendarId == ALL_TIME) {
				filter.calendarId = ALL_TIME
				delete filter.periodId1
				delete filter.periodId2
				delete filter.periodDelta1
				delete filter.periodDelta2
			} else {
				filter.calendarId = val.calendarId

				if (val.relative) {
					filter.periodDelta1 = val.first
					filter.periodDelta2 = val.last
					delete filter.periodId1
					delete filter.periodId2
				} else {
					filter.periodId1 = val.first
					filter.periodId2 = val.last
					delete filter.periodDelta1
					delete filter.periodDelta2
				}
			}
		}
		return filter
	},

	getFilterField: function() {
		const fieldId = _.get(this.options, 'filter.fieldId')
		// #46235 prevent errors if no model, or model without combinedFieldsById
		// (for example, when this component is during import transformations)
		let combinedFieldsById = _.get(this.model, 'combinedFieldsById', [])
		return combinedFieldsById[fieldId]
	},

	getFilterDataType: function() {
		return _.get(this.getFilterField(), 'dataType')
	},

	remove: function () {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([ this.toggle, ...this.dateValueOrEquations, this.rangeSelector ],
			'remove')
	}
})

export default DatasetsDateFilterPicker

DatasetsDateFilterPicker.__filename__ = 'datasets/filters/datasets-date-filter-picker.js'
